/**
 * VENUE CHECK-IN FLOW PAGE
 * Matches screenshot: Dark header, simulation pills, venue card, and scan trigger.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { venuesAPI, roadsAPI } from '../api';
import QRScanner from '../components/QRScanner';
import { BottomNav } from './Home';
import LoadingSpinner from '../components/LoadingSpinner';

const T = {
    ink: '#1a2234',
    inkMid: '#243048',
    copper: '#b8753d',
    copperDark: '#8d5d36',
    smoke: '#8899aa',
    cream: '#f5ede4',
    success: '#2e7d32',
    danger: '#c62828'
};

export default function CheckIn() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [checkinResult, setCheckinResult] = useState(null);
    const [gpsDebug, setGpsDebug] = useState(null);



    const handleScanSuccess = async (token) => {
        setShowScanner(false);
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const res = await venuesAPI.submitCheckin(token, position.coords.latitude, position.coords.longitude);
                    setCheckinResult({ success: true, message: res.data.message });
                    setTimeout(() => window.location.href = '/profile', 2000);
                } catch (err) {
                    setCheckinResult({ 
                        success: false, 
                        message: err.response?.data?.message || err.response?.data?.error || 'Check-in failed' 
                    });
                } finally {
                    setLoading(false);
                }
            },
            () => {
                setCheckinResult({ success: false, message: 'Location access required for check-in.' });
                setLoading(false);
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div style={s.screen}>
            {/* Dark Header */}
            <div style={s.header}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1 style={s.title}>Venue Check-In</h1>
                        <p style={s.subtitle}>Scan the venue's QR code to log your visit</p>
                    </div>
                    {/* Real Scanner Toggle */}
                    <button 
                        onClick={() => setShowScanner(true)}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 12, padding: 10, cursor: 'pointer' }}
                        title="Open Real Camera"
                    >
                        <span style={{ fontSize: 20 }}>📷</span>
                    </button>
                </div>
            </div>

            <div style={s.content}>
                {/* Main Action Button - Now Real Scanner */}
                <button style={s.scanBtn} onClick={() => setShowScanner(true)}>
                    📷 Tap to scan QR code
                </button>
                

                
                <p style={s.note}>Point your camera at the venue QR code to earn your badge</p>



                {checkinResult && (
                    <div style={{ ...s.toast, background: checkinResult.success ? T.success : T.danger }}>
                        {checkinResult.message}
                    </div>
                )}
            </div>

            {showScanner && (
                <div style={s.scannerModal}>
                   <QRScanner 
                        key={showScanner}
                        onScanSuccess={handleScanSuccess} 
                        onClose={() => setShowScanner(false)} 
                    />
                </div>
            )}

            <BottomNav active="/checkin" />
        </div>
    );
}

const s = {
    // ... (rest of styles)
    scannerModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 70, zIndex: 3000, background: '#000' },
    screen: { background: '#f5f0e6', minHeight: '100vh', paddingBottom: 100, fontFamily: 'Inter, sans-serif' },
    header: { background: '#131e24', padding: '60px 24px 30px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
    title: { color: '#fff', fontSize: 28, fontFamily: 'Georgia, serif', margin: 0, fontWeight: 'bold' },
    subtitle: { color: '#8899aa', fontSize: 13, marginTop: 8 },
    
    content: { padding: '24px 20px' },
    simLabel: { fontSize: 11, fontWeight: '800', color: '#a1a1aa', letterSpacing: 1.5, marginBottom: 14 },
    pillRow: { display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, marginBottom: 4 },
    pill: { 
        padding: '10px 16px', borderRadius: 12, fontSize: 13, fontWeight: '600', 
        color: '#1a2234', display: 'flex', alignItems: 'center', cursor: 'pointer',
        whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
    },
    progressLine: { height: 6, background: '#d8ccbe', width: '70%', borderRadius: 3, margin: '14px 0 24px', opacity: 0.6 },

    card: { 
        background: '#fff', borderRadius: 24, padding: 24, 
        border: '1px solid #e6dfd3', boxShadow: '0 12px 32px rgba(0,0,0,0.04)',
        marginBottom: 32
    },
    venueRow: { display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 },
    venueIcon: { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    venueInfo: { flex: 1 },
    venueName: { fontSize: 17, fontWeight: 'bold', color: '#1a2234' },
    venueTagline: { fontSize: 12, color: '#8899aa', marginTop: 3 },
    venueLoc: { fontSize: 11, color: '#b8753d', fontWeight: 'bold', marginTop: 4, textTransform: 'uppercase' },

    qrArea: { background: '#efeeea', borderRadius: 16, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    qrMock: { opacity: 0.3, letterSpacing: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' },
    qrDots: { fontSize: 24, fontWeight: 'bold', lineHeight: 0.8 },

    scanBtn: {
        width: '100%', padding: '18px', borderRadius: 40, border: 'none',
        background: 'linear-gradient(135deg, #7c4c2a 0%, #5c351b 100%)',
        color: '#fff', fontWeight: 'bold', fontSize: 17, cursor: 'pointer',
        boxShadow: '0 10px 24px rgba(124, 76, 42, 0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
    },
    note: { textAlign: 'center', fontSize: 12, color: '#8899aa', marginTop: 18, fontWeight: '500' },
    
    toast: { 
        marginTop: 20, padding: 16, borderRadius: 12, color: '#fff', 
        textAlign: 'center', fontWeight: 'bold', fontSize: 14 
    }
};

